import { UnitValue } from "@vattenfall/core";

/**
 * The payload for a product record as returned from the Finnish product API
 */
export class FinnishProductDetailsModel {
  /**
   * The selected product identifier
   */
  productId?: string;
  /**
   * The type of product (fixed, variable or spot )
   */
  productType?: "fixed" | "variable" | "spot";
  /**
   * The name of the agreement
   */
  agreementName?: string;
  /**
   * The monthly base fee
   */
  monthlyBaseFee?: UnitValue;
  /**
   * The discounted monthly base fee
   */
  discountedMonthlyBaseFee?: UnitValue;
  /**
   * The electricity price
   */
  electricityPrice?: UnitValue;
  /**
   * The length of the contract
   */
  contractLength?: UnitValue;
  /**
   * The length of discounted period
   */
  discountLength?: UnitValue;
  /**
   * Whether the montly cost should be hidden in view or not
   */
  hideMonthlyCost?: boolean;
  /**
   * Energy source name
   */
  energySourceName?: string;
  /**
   * The origin fee for energy source
   */
  originFee?: UnitValue;
  /**
   * To show or hide the electricity price data
   */
  hideElectricityPriceRow?: boolean;
}

/**
 * The payload for a product record as returned from the Finnish product API along with getters for consumption, price and co2 emission calculations
 */
export class FinnishProductDetailsExtendedModel extends FinnishProductDetailsModel {
  /**
   * Estimated kWh consumption (typically supplied as an input parameter)
   */
  estimatedKwhConsumption = 0;
  /**
   * Estimated kWh consumption expressed as a unit value
   */
  get estimatedKwhConsumptionUnit(): UnitValue {
    return {
      value: this.estimatedKwhConsumption,
      unit: "KWH",
    };
  }
  /**
   * Estimated Co2 emissions per month expressed as a unit value
   */
  get estimatedCo2EmissionPerMonthUnit(): UnitValue {
    const CO2_EMISSION_RATIO = 0.264;
    return {
      value: this.estimatedKwhConsumptionPerMonth * CO2_EMISSION_RATIO,
      unit: "KG/MONTH",
    };
  }
  /**
   * Estimated electricity price per month expressed as a unit value
   */
  get estimatedElectricityPricePerMonthUnit(): UnitValue {
    return {
      value: this.monthlyFee + this.estimatedKwhConsumptionPerMonth * (this.electricityPriceFixed / 100),
      unit: "EURO/MONTH",
    } as UnitValue;
  }
  /**
   * Estimated kWh consumption per month
   */
  get estimatedKwhConsumptionPerMonth(): number {
    return this.estimatedKwhConsumption / 12;
  }
  /**
   * Electricity price as a floating number with two digits
   */
  get electricityPriceFixed(): number {
    return +(this.electricityPrice?.value?.toFixed(2) ?? 0);
  }
  /**
   * Monthly discounted or base fee, whichever is lower
   */
  get monthlyFee(): number {
    return this.discountedMonthlyBaseFee?.value ?? this.monthlyBaseFee?.value ?? 0;
  }
  /**
   * Whether the price is discounted or not
   */
  get isDiscountedProduct(): boolean {
    return this.discountedMonthlyBaseFee != null ? true : false;
  }
}
